import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Briefcase,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  DollarSign,
  Mail,
  MessageCircle,
  Phone,
  Play,
  Search,
  Send,
  Square,
  X,
} from 'lucide-react';
import { messagesApi } from '../../api/messagesApi';
import { providerBookingApi } from '../../api/providerBookingApi';
import PayoutStatement from '../../components/PayoutStatement';
import ServiceIcon from '../../components/ServiceIcon';
import { ProviderLayout } from '../../components/layouts';
import { useAuth } from '../../context/AuthContext';
import { connectSocket, getSocket } from '../../socket/socketClient';
import { loadGoogleMaps } from '../../utils/loadGoogleMaps';
import { formatHours } from '../../utils/duration';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID?.()) ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const GROUPS = {
  pending: ['pending'],
  active: ['accepted', 'in_progress'],
  completed: ['completed'],
  cancelled: ['cancelled', 'auto_cancelled', 'declined', 'lapsed'],
};
const groupOf = (status) => Object.keys(GROUPS).find((group) => GROUPS[group].includes(status)) || 'other';
const GROUP_ORDER = { pending: 0, active: 1, completed: 2, cancelled: 3, other: 4 };
const STATUS_PILLS = [
  { key: 'pending', label: 'Needs response' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const STATUS_META = {
  pending: { label: 'Pending', className: 'bg-amber-500/[0.12] text-amber-700' },
  accepted: { label: 'Accepted', className: 'bg-giggs-green/[0.12] text-giggs-green' },
  in_progress: { label: 'In progress', className: 'bg-[#185FA5]/[0.12] text-[#185FA5]' },
  completed: { label: 'Completed', className: 'bg-giggs-green/[0.12] text-giggs-green' },
  cancelled: { label: 'Cancelled', className: 'bg-red-500/10 text-[#A32D2D]' },
  auto_cancelled: { label: 'Auto-cancelled', className: 'bg-red-500/10 text-[#A32D2D]' },
  declined: { label: 'Declined', className: 'bg-black/[0.05] text-[#6B7C73]' },
  lapsed: { label: 'No response', className: 'bg-amber-500/[0.12] text-amber-700' },
};

const fmtDate = (date) =>
  date ? new Date(date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
const fmtDateTime = (date) =>
  date ? new Date(date).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }) : '';
const fmtDeadline = (deadline) => {
  const date = new Date(deadline);
  if (date <= new Date()) return 'Response window closed';
  return `Respond by ${date.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })}`;
};
const remaining = (deadline) => {
  const ms = new Date(deadline) - new Date();
  if (ms <= 0) return 'closing now';
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  return hours >= 1 ? `${hours}h ${mins % 60}m left` : `${mins % 60}m left`;
};

const baseButton = 'inline-flex cursor-pointer items-center justify-center gap-2 rounded-[10px] border px-4 py-2.5 font-dm text-[13px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50';
const primaryButton = `${baseButton} border-giggs-green bg-giggs-green text-white hover:bg-giggs-green-hover`;
const ghostButton = `${baseButton} border-[#D5DFD9] bg-white text-[#41584d] hover:bg-slate-50`;
const dangerButton = `${baseButton} border-red-600 bg-red-600 text-white hover:bg-red-700`;
const inputClass = 'box-border w-full rounded-[10px] border border-[#D5DFD9] bg-white px-3.5 py-2.5 font-dm text-sm text-giggs-ink outline-none transition focus:border-giggs-green focus:ring-2 focus:ring-giggs-green/10';
const cardClass = 'rounded-2xl border border-[#E3EAE6] bg-white p-4 sm:p-[18px]';
const sectionTitleClass = 'mb-3 font-nunito text-xs font-extrabold uppercase tracking-[0.05em] text-giggs-muted';

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold ${meta.className}`}>{meta.label}</span>;
};

const MiniMap = ({ lat, lng }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!MAPS_KEY || lat == null || lng == null) return undefined;
    let cancelled = false;
    loadGoogleMaps(MAPS_KEY)
      .then((maps) => {
        if (cancelled || !ref.current) return;
        const map = new maps.Map(ref.current, {
          center: { lat, lng },
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        new maps.Marker({ map, position: { lat, lng } });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lat, lng]);
  if (lat == null || lng == null) return null;
  return <div ref={ref} className="h-[200px] w-full overflow-hidden rounded-xl" />;
};

const buildTimeline = (booking) => {
  const nodes = [{ key: 'req', label: 'Requested', detail: `${booking.client?.name || 'The client'} requested this booking`, state: 'done' }];
  switch (booking.status) {
    case 'pending':
      nodes.push({ key: 'resp', label: 'Your response needed', state: 'current', chip: booking.responseDeadline ? remaining(booking.responseDeadline) : null, detail: booking.responseDeadline ? fmtDeadline(booking.responseDeadline) : 'Accept or decline this request' });
      nodes.push({ key: 'prog', label: 'In progress', detail: 'Starts on the day, once accepted', state: 'upcoming' });
      nodes.push({ key: 'done', label: 'Completed', detail: 'Job finished', state: 'upcoming' });
      break;
    case 'accepted':
      nodes.push({ key: 'resp', label: 'Accepted', detail: 'You accepted this booking', state: 'current' });
      nodes.push({ key: 'prog', label: 'In progress', detail: `Starts ${fmtDate(booking.scheduledDate)}`, state: 'upcoming' });
      nodes.push({ key: 'done', label: 'Completed', detail: 'Job finished', state: 'upcoming' });
      break;
    case 'in_progress':
      nodes.push({ key: 'resp', label: 'Accepted', detail: 'You accepted this booking', state: 'done' });
      nodes.push({ key: 'prog', label: 'In progress', detail: 'Job underway', sub: fmtDateTime(booking.startConfirmedAt), state: 'current' });
      nodes.push({ key: 'done', label: 'Completed', detail: 'Job finished', state: 'upcoming' });
      break;
    case 'completed':
      nodes.push({ key: 'resp', label: 'Accepted', detail: 'You accepted this booking', state: 'done' });
      nodes.push({ key: 'prog', label: 'Started', detail: 'Job underway', sub: fmtDateTime(booking.startConfirmedAt), state: 'done' });
      nodes.push({ key: 'done', label: 'Completed', detail: booking.review?.rating ? `Client rated you ${booking.review.rating}★` : 'Job finished', sub: fmtDateTime(booking.endConfirmedAt), state: 'current' });
      break;
    case 'declined':
      nodes.push({ key: 'resp', label: 'You declined', detail: 'You turned down this request', state: 'warn' });
      break;
    case 'lapsed':
      nodes.push({ key: 'resp', label: 'No response', detail: 'The response window passed before you replied', state: 'warn' });
      break;
    case 'cancelled':
    case 'auto_cancelled': {
      if (booking.startConfirmedAt) {
        nodes.push({ key: 'resp', label: 'Accepted', detail: 'You accepted this booking', state: 'done' });
        nodes.push({ key: 'prog', label: 'Started', detail: 'Job underway', sub: fmtDateTime(booking.startConfirmedAt), state: 'done' });
      }
      const cancellation = booking.cancellation || {};
      const by = cancellation.by === 'client' ? 'the client' : cancellation.by === 'gigger' ? 'you' : cancellation.by === 'admin' ? 'an admin' : null;
      const reasonMap = {
        gigger_no_show: 'reported no-show',
        client_unavailable: 'client no longer available',
        gigger_cancelled: 'you cancelled',
        other: cancellation.detail || 'other reason',
      };
      nodes.push({ key: 'cx', label: booking.status === 'auto_cancelled' ? 'Auto-cancelled' : `Cancelled${by ? ` by ${by}` : ''}`, detail: cancellation.reason ? (reasonMap[cancellation.reason] || cancellation.detail || '') : (cancellation.detail || ''), sub: fmtDateTime(cancellation.at), state: 'fail' });
      break;
    }
    default:
      break;
  }
  return nodes;
};

const TimelineDot = ({ state }) => {
  const stateClass = {
    done: 'bg-giggs-green text-white',
    current: 'bg-[#185FA5] text-white',
    warn: 'bg-amber-500 text-white',
    fail: 'bg-red-600 text-white',
    upcoming: 'border border-[#D5DFD9] bg-white text-giggs-faint',
  }[state] || 'border border-[#D5DFD9] bg-white text-giggs-faint';
  return (
    <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${stateClass}`}>
      {state === 'done' && <Check size={15} strokeWidth={3} />}
      {state === 'current' && <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-white" />}
      {state === 'warn' && <AlertTriangle size={15} />}
      {state === 'fail' && <X size={15} strokeWidth={3} />}
      {state === 'upcoming' && <span className="h-2.5 w-2.5 rounded-full border border-giggs-faint" />}
    </span>
  );
};

const JobChat = ({ conversationId, myId, clientName }) => {
  const [thread, setThread] = useState([]);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);

  const mapMsg = useCallback((message) => ({
    _id: message._id ? String(message._id) : null,
    from: !message.fromGuest && String(message.sender?._id) === String(myId) ? 'me' : 'them',
    text: message.content,
    clientMessageId: message.clientMessageId || null,
  }), [myId]);

  useEffect(() => {
    if (!conversationId) return undefined;
    let active = true;
    const socket = getSocket() || connectSocket();
    socket?.emit('conversation:join', { conversationId });
    messagesApi.getMessages(conversationId)
      .then((response) => { if (active) setThread((response.data?.data || []).map(mapMsg)); })
      .catch(() => {});
    messagesApi.markRead(conversationId).catch(() => {});

    const onMessage = (message) => {
      if (String(message.conversation) !== String(conversationId)) return;
      setThread((current) => {
        const id = String(message._id);
        if (current.some((item) => item._id && String(item._id) === id)) return current;
        const mapped = mapMsg(message);
        if (message.clientMessageId && current.some((item) => item.clientMessageId === message.clientMessageId)) {
          return current.map((item) => item.clientMessageId === message.clientMessageId ? { ...mapped, clientMessageId: message.clientMessageId } : item);
        }
        return [...current, mapped];
      });
    };
    socket?.on('message:new', onMessage);
    return () => { active = false; socket?.off('message:new', onMessage); };
  }, [conversationId, mapMsg]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const send = (event) => {
    event?.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    const clientMessageId = uid();
    setThread((current) => [...current, { _id: null, from: 'me', text, clientMessageId, pending: true }]);
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit('message:send', { conversationId, content: text, type: 'text', clientMessageId }, (ack) => {
        if (ack?.ok && ack.data) setThread((current) => current.map((message) => message.clientMessageId === clientMessageId ? mapMsg(ack.data) : message));
      });
    } else {
      messagesApi.sendMessage(conversationId, { content: text, type: 'text', clientMessageId })
        .then((response) => response.data?.data && setThread((current) => current.map((message) => message.clientMessageId === clientMessageId ? mapMsg(response.data.data) : message)))
        .catch(() => {});
    }
  };

  return (
    <section className={`${cardClass} flex h-[clamp(380px,58vh,620px)] min-h-0 flex-col p-0`}>
      <div className="flex items-center gap-2 border-b border-[#EEF2EF] px-4 py-3.5 text-[13px] font-bold text-[#10271d]"><MessageCircle size={17} className="text-giggs-green" /> Chat with {clientName || 'client'}</div>
      <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-slate-50 p-3.5">
        {thread.length === 0 ? (
          <div className="flex h-full items-center justify-center text-center text-xs text-giggs-muted">No messages yet. Say hello!</div>
        ) : thread.map((message, index) => (
          <div key={message._id ? `${message._id}` : message.clientMessageId || `t${index}`} className={`flex ${message.from === 'me' ? 'justify-end' : 'justify-start'} ${message.pending ? 'opacity-60' : ''}`}>
            <div className={`max-w-[86%] rounded-2xl px-3.5 py-2.5 text-sm leading-5 ${message.from === 'me' ? 'rounded-br bg-giggs-green text-white' : 'rounded-bl border border-[#E3EAE6] bg-white text-[#2D3B35]'}`}>{message.text}</div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-[#EEF2EF] bg-white p-3">
        <input className={`${inputClass} min-w-0 flex-1`} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Type a message…" />
        <button type="submit" className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border-0 bg-giggs-green text-white hover:bg-giggs-green-hover" aria-label="Send message"><Send size={16} /></button>
      </form>
    </section>
  );
};

const DetailRow = ({ label, value, icon }) => (
  <div className="flex flex-col gap-1 border-b border-[#EEF2EF] py-2.5 last:border-b-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-giggs-muted">{icon}{label}</span>
    <span className="min-w-0 break-words text-left text-[13px] font-semibold text-[#10271d] sm:max-w-[68%] sm:text-right">{value}</span>
  </div>
);

const PriceRow = ({ label, value, extra, total }) => (
  <div className={`flex items-center justify-between gap-4 py-1.5 ${extra ? 'text-giggs-green' : ''}`}>
    <span className={`text-[13px] ${total ? 'font-bold text-[#10271d]' : 'text-giggs-muted'}`}>{label}</span>
    <span className={`${total ? 'text-lg font-extrabold text-[#10271d]' : 'text-[13px] font-bold text-[#10271d]'} ${extra ? '!text-giggs-green' : ''}`}>{value}</span>
  </div>
);

const TaskExpectationSummary = ({ booking, compact = false }) => {
  const selected = Array.isArray(booking.selectedTasks) ? booking.selectedTasks : [];
  const serviceItems = Array.isArray(booking.serviceItems) ? booking.serviceItems : [];
  const groups = serviceItems.length > 0
    ? serviceItems
    : [{ name: booking.service?.name || 'Service', tasks: selected, plannedHours: booking.hours }];
  const hasTasks = groups.some((item) => (item.tasks || []).length > 0);
  if (!hasTasks && !booking.taskNotes) return null;
  // Sub services are labels for the work — only the booked hours are priced.
  const booked = Number(booking.hours ?? booking.pricing?.hours) || 0;
  return (
    <section className={compact ? 'mt-4 rounded-xl border border-giggs-green/20 bg-giggs-green/[0.06] p-3.5' : cardClass}>
      <div className={sectionTitleClass}>Requested work</div>
      <div className="flex flex-col gap-3">
        {groups.map((group, groupIndex) => (
          <div key={String(group.service || group.name || groupIndex)} className="rounded-xl border border-[#E3EAE6] bg-[#F8FAF9] p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[12px] font-extrabold text-[#10271d]">{group.name || 'Service'}</span>
              {Number(group.plannedHours) > 0 && <span className="text-[10px] font-bold text-giggs-green">{formatHours(group.plannedHours)} booked</span>}
            </div>
            {(group.tasks || []).length > 0 ? (
              <div className="flex flex-col gap-2">
                {(group.tasks || []).map((task) => (
                  <div key={`${group.name}-${task.name}`} className="rounded-[10px] border border-[#E3EAE6] bg-white px-3 py-2.5">
                    <span className="min-w-0 break-words text-[13px] font-semibold text-[#10271d]">{task.name}</span>
                  </div>
                ))}
              </div>
            ) : <div className="text-[11px] text-giggs-muted">No sub services supplied.</div>}
          </div>
        ))}
      </div>
      {booked > 0 && <div className="mt-3 text-xs font-bold text-[#177552]">Booked time: {formatHours(booked)}</div>}
      {booking.taskNotes && <div className="mt-3 rounded-[10px] bg-slate-50 px-3 py-2.5"><div className="text-[10px] font-bold uppercase tracking-[0.05em] text-giggs-muted">Client instructions</div><div className="mt-1 whitespace-pre-line break-words text-xs leading-5 text-[#41584d]">{booking.taskNotes}</div></div>}
    </section>
  );
};
const JobDetail = ({ booking, busy, act, onBack, myId }) => {
  const [cancelDetail, setCancelDetail] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const timeline = buildTimeline(booking);
  const tasks = booking.extraHoursRequests || [];
  const pendingExtras = tasks.filter((request) => request.status === 'pending');
  const historyTasks = tasks.filter((request) => request.status !== 'pending');
  const hasUnpaidAcceptedExtras = tasks.some((request) => request.status === 'accepted' && !request.paidAt);
  const addOnPaymentDue = hasUnpaidAcceptedExtras && booking.payment?.balanceStatus !== 'paid';
  const rate = booking.pricing?.hourlyRate || 0;
  const baseHours = booking.pricing?.hours || 0;
  const extraHours = booking.pricing?.extraHours || 0;
  const address = [booking.location?.address, booking.location?.suburb, booking.location?.postcode].filter(Boolean).join(', ');

  return (
    <div className="font-dm text-[#2D3B35]">
      <button type="button" className="mb-4 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[13px] font-semibold text-giggs-green hover:text-giggs-green-hover" onClick={onBack}><ArrowLeft size={16} /> All bookings</button>

      <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-[#E3EAE6] bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-[18px]">
        <div className="min-w-0">
          <div className="break-words font-nunito text-lg font-extrabold text-[#10271d]">{booking.service?.name}</div>
          <small className="mt-0.5 block break-all text-xs font-medium text-giggs-muted">{booking.trackingId}</small>
        </div>
        <StatusBadge status={booking.status} />
      </div>

      <div className={`grid min-w-0 gap-4 ${booking.conversation ? 'xl:grid-cols-[minmax(220px,0.78fr)_minmax(300px,1.15fr)_minmax(260px,0.95fr)]' : 'lg:grid-cols-[minmax(220px,0.8fr)_minmax(0,1.2fr)]'}`}>
        <aside className="min-w-0 space-y-4">
          <section className={cardClass}>
            <div className={sectionTitleClass}>Client &amp; schedule</div>
            <DetailRow label="Client" value={booking.client?.name} />
            {booking.client?.phone && <DetailRow label="Phone" icon={<Phone size={13} />} value={booking.client.phone} />}
            {booking.client?.email && <DetailRow label="Email" icon={<Mail size={13} />} value={booking.client.email} />}
            <DetailRow label="Date" value={fmtDate(booking.scheduledDate)} />
            <DetailRow label="Time" value={`${booking.startTime} – ${booking.endTime}`} />
            <DetailRow label="Address" value={address || 'Revealed once you accept'} />
          </section>

          <section className={cardClass}>
            <div className={sectionTitleClass}>Your payout</div>
            <PriceRow label={`$${rate}/hr × ${baseHours} hr`} value={`$${(rate * baseHours).toFixed(2)}`} />
            {extraHours > 0 && <PriceRow extra label={`Additional sub services (${extraHours} hr)`} value={`+$${(rate * extraHours).toFixed(2)}`} />}
            <div className="my-2 border-t border-[#EEF2EF]" />
            <PriceRow total label="Payout" value={`$${booking.pricing?.payout}`} />
          </section>

          {booking.location?.lat != null && (
            <section className="overflow-hidden rounded-2xl border border-[#E3EAE6] bg-white p-0"><MiniMap lat={booking.location.lat} lng={booking.location.lng} /></section>
          )}
        </aside>

        <section className="min-w-0 space-y-4">
          <TaskExpectationSummary booking={booking} />

          <section className={cardClass}>
            <div className={sectionTitleClass}>Progress</div>
            <ol className="m-0 list-none p-0">
              {timeline.map((node, index) => (
                <li key={node.key} className="relative flex gap-3.5 pb-5 last:pb-0">
                  {index < timeline.length - 1 && <span className="absolute bottom-0 left-[15px] top-8 w-px bg-[#E3EAE6]" />}
                  <TimelineDot state={node.state} />
                  <div className="min-w-0 flex-1 pt-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[13px] font-bold text-[#10271d]">{node.label}</span>
                      {node.chip && <span className="rounded-full bg-amber-500/[0.12] px-2.5 py-1 text-[11px] font-bold text-amber-700">{node.chip}</span>}
                    </div>
                    {node.detail && <div className="mt-1 break-words text-xs leading-5 text-giggs-muted">{node.detail}</div>}
                    {node.sub && <div className="mt-1 text-[11px] text-giggs-faint">{node.sub}</div>}
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {booking.status === 'pending' && (
            <section className={cardClass}>
              <div className="mb-2 text-sm font-bold text-[#10271d]">Respond to this request</div>
              <div className="mb-3 text-[13px] leading-5 text-giggs-muted">Review the requested sub services and instructions above before accepting. Accepting reveals the client's contact and exact address.</div>
              <div className="flex flex-col gap-2.5 sm:flex-row">
                <button type="button" disabled={busy === 'acc'} className={`${primaryButton} flex-1`} onClick={() => act('acc', () => providerBookingApi.respond(booking._id, 'accept'))}><Check size={16} /> Accept</button>
                <button type="button" disabled={busy === 'dec'} className={`${ghostButton} flex-1`} onClick={() => act('dec', () => providerBookingApi.respond(booking._id, 'decline'))}><X size={16} /> Decline</button>
              </div>
            </section>
          )}

          {booking.status === 'accepted' && (
            <section className={cardClass}>
              <div className="mb-2 text-sm font-bold text-[#10271d]">Ready to begin?</div>
              {booking.payment?.depositStatus === 'paid' && !addOnPaymentDue ? (
                <>
                  <div className="mb-3 text-[13px] leading-5 text-giggs-muted">Start the job when you're on site and underway.</div>
                  <button type="button" disabled={busy === 'start'} className={`${primaryButton} w-full`} onClick={() => act('start', () => providerBookingApi.startJob(booking._id))}><Play size={16} /> Start job</button>
                </>
              ) : addOnPaymentDue ? (
                <>
                  <div className="mb-3 text-[13px] leading-5 text-giggs-muted">The client must pay accepted additional sub services in advance before the job starts.</div>
                  <button type="button" disabled className={`${primaryButton} w-full opacity-55`}><Clock size={16} /> Awaiting additional payment</button>
                </>
              ) : (
                <>
                  <div className="mb-3 text-[13px] leading-5 text-giggs-muted">Waiting for the client to pay the full booking amount. You can start once it clears — this updates automatically.</div>
                  <button type="button" disabled className={`${primaryButton} w-full opacity-55`}><Clock size={16} /> Awaiting full client payment</button>
                </>
              )}
            </section>
          )}

          {booking.status === 'in_progress' && (
            <section className={cardClass}>
              <div className="mb-2 text-sm font-bold text-[#10271d]">Job in progress</div>
              {pendingExtras.length > 0 ? (
                <>
                  <div className="mb-3 text-[13px] leading-5 text-giggs-muted">Respond to every additional sub service below before completing the job.</div>
                  <button type="button" disabled className={`${primaryButton} w-full opacity-55`}><Clock size={16} /> Additional sub-service response required</button>
                </>
              ) : addOnPaymentDue ? (
                <>
                  <div className="mb-3 text-[13px] leading-5 text-giggs-muted">Accepted additional sub services must be paid by the client before you complete the job.</div>
                  <button type="button" disabled className={`${primaryButton} w-full opacity-55`}><Clock size={16} /> Awaiting additional payment</button>
                </>
              ) : (
                <>
                  <div className="mb-3 text-[13px] leading-5 text-giggs-muted">End the job once the work is complete.</div>
                  <button type="button" disabled={busy === 'end'} className={`${primaryButton} w-full`} onClick={() => act('end', () => providerBookingApi.endJob(booking._id))}><Square size={16} /> End job</button>
                </>
              )}
            </section>
          )}

          {booking.status === 'completed' && <PayoutStatement bookingId={booking._id} />}





          {['accepted', 'in_progress'].includes(booking.status) && (
            showCancel ? (
              <section className={`${cardClass} border-red-500`}>
                <div className="mb-2.5 flex items-center gap-1.5 text-sm font-bold text-[#A32D2D]"><AlertTriangle size={16} /> Cancelling adds a strike to your account</div>
                <textarea rows={2} value={cancelDetail} onChange={(event) => setCancelDetail(event.target.value)} placeholder="Reason (optional)" className={`${inputClass} mb-2.5 min-h-20 resize-y`} />
                <div className="flex flex-col gap-2.5 sm:flex-row">
                  <button type="button" disabled={busy === 'cancel'} className={`${dangerButton} flex-1`} onClick={() => act('cancel', () => providerBookingApi.cancel(booking._id, cancelDetail)).then(() => onBack())}>Confirm cancel</button>
                  <button type="button" className={`${ghostButton} flex-1`} onClick={() => setShowCancel(false)}>Keep job</button>
                </div>
              </section>
            ) : (
              <div className="text-center"><button type="button" className="cursor-pointer border-0 bg-transparent p-0 text-[13px] font-semibold text-red-600 hover:underline" onClick={() => setShowCancel(true)}>Cancel this job</button></div>
            )
          )}
        </section>
        <aside className="min-w-0">
        {booking.conversation && (
          <JobChat conversationId={booking.conversation} myId={myId} clientName={booking.client?.name} />
        )}
        {pendingExtras.length > 0 && (
            <section className={`${cardClass} border-amber-500`}>
              <div className="mb-2 text-sm font-bold text-amber-700">More hours requested ({pendingExtras.length})</div>
              <div className="space-y-3">
                {pendingExtras.map((pendingExtra) => (
                  <div key={pendingExtra._id} className="rounded-xl bg-amber-50/70 p-3">
                    <div className="break-words text-sm font-semibold text-[#10271d]">{formatHours(pendingExtra.hours)} more</div>
                    {pendingExtra.serviceName && <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-giggs-green">{pendingExtra.serviceName}</div>}
                    {(pendingExtra.taskNames || []).length > 0 && <div className="mt-1 break-words text-xs leading-5 text-[#10271d]">For: {pendingExtra.taskNames.join(', ')}</div>}
                    {pendingExtra.details && <div className="mt-1 break-words text-xs leading-5 text-giggs-muted">{pendingExtra.details}</div>}
                    <div className="mt-3 flex flex-col gap-2.5 sm:flex-row sm:items-center">
                      <button type="button" disabled={busy === `exAcc:${pendingExtra._id}`} className={`${primaryButton} flex-1`} onClick={() => act(`exAcc:${pendingExtra._id}`, () => providerBookingApi.respondExtraHours(booking._id, pendingExtra._id, 'accept'))}>Accept</button>
                      <button type="button" disabled={busy === `exDec:${pendingExtra._id}`} className={ghostButton} onClick={() => act(`exDec:${pendingExtra._id}`, () => providerBookingApi.respondExtraHours(booking._id, pendingExtra._id, 'decline'))}>Decline</button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs leading-5 text-giggs-muted">Respond to each request separately. The client pays for accepted hours in advance before the extra work starts.</div>
            </section>
          )}

                    {historyTasks.length > 0 && (
            <section className={cardClass}>
              <div className={sectionTitleClass}>Additional sub services</div>
              <div className="flex flex-col gap-2">
                {historyTasks.map((task) => (
                  <div className="rounded-[10px] border border-[#E8EDE9] bg-slate-50 px-3 py-2.5" key={task._id}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0"><div className="break-words text-[13px] font-semibold text-[#10271d]">{task.taskName || task.description}</div>{task.serviceName && <div className="mt-1 text-[10px] font-bold uppercase tracking-wide text-giggs-green">{task.serviceName}</div>}{task.details && <div className="mt-1 break-words text-[11px] leading-4 text-giggs-muted">{task.details}</div>}</div>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${task.status === 'accepted' ? 'bg-giggs-green/10 text-giggs-green' : 'bg-red-500/10 text-red-500'}`}>{task.status === 'accepted' ? (task.paidAt ? 'Paid' : 'Awaiting payment') : 'Declined'}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-giggs-muted">{task.status === 'accepted' ? `+${task.hours} hr · +${((task.hours || 0) * (task.hourlyRate || rate)).toFixed(2)} payout` : `Expected ${task.hours || 0.5} hr · Declined`}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          
        </aside>
      </div>
    </div>
  );
};

const RespondModal = ({ booking, busy, act, onClose }) => {
  const hours = booking.hours || 0;
  const rate = hours ? Number(booking.payout) / hours : 0;
  const area = [booking.location?.suburb, booking.location?.state, booking.location?.postcode].filter(Boolean).join(' ');
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-[#070F0B]/60 p-3 backdrop-blur-sm sm:p-5" onClick={onClose}>
      <section className="my-auto max-h-[calc(100dvh-2rem)] w-full max-w-[560px] overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 border-b border-[#EEF2EF] px-4 py-4 sm:px-5">
          <div className="min-w-0 font-nunito text-lg font-extrabold text-[#10271d]">{booking.service?.name}<small className="mt-0.5 block break-all font-dm text-xs font-medium text-giggs-muted">{booking.trackingId}</small></div>
          <div className="flex shrink-0 items-center gap-2.5"><StatusBadge status={booking.status} /><button type="button" className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-giggs-muted hover:text-[#10271d]" onClick={onClose} aria-label="Close"><X size={20} /></button></div>
        </div>
        <div className="p-4 sm:p-5">
          <div className="mb-4">
            <div className={sectionTitleClass}>Request details</div>
            <DetailRow label="Client" value={booking.clientName} />
            <DetailRow label="Date" value={fmtDate(booking.scheduledDate)} />
            <DetailRow label="Time" value={`${booking.startTime} – ${booking.endTime}`} />
            <DetailRow label="Area" value={area || '—'} />
          </div>

          <TaskExpectationSummary booking={booking} compact />

          <div className="mb-3.5 mt-4 flex items-center justify-between gap-4 rounded-xl bg-giggs-green/[0.08] px-4 py-3.5">
            <div className="min-w-0"><div className="text-[13px] font-semibold text-[#177552]">${rate ? rate.toFixed(0) : '—'}/hr × {hours} hr</div><small className="block text-[11px] font-medium text-[#177552]/75">Your payout for this job</small></div>
            <div className="shrink-0 font-nunito text-[22px] font-extrabold text-[#177552]">${booking.payout}</div>
          </div>

          {booking.responseDeadline && <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700"><Clock size={13} /> {fmtDeadline(booking.responseDeadline)}</div>}
          <div className="mb-1 mt-3 text-[12.5px] leading-5 text-giggs-muted">Review the requested work before accepting. Accepting reveals the client's contact and exact address, and opens the job chat.</div>
          <div className="mt-3 flex flex-col gap-2.5 sm:flex-row">
            <button type="button" disabled={busy === 'acc'} className={`${primaryButton} flex-1`} onClick={() => act('acc', () => providerBookingApi.respond(booking._id, 'accept')).then(onClose)}><Check size={16} /> Accept</button>
            <button type="button" disabled={busy === 'dec'} className={`${ghostButton} flex-1`} onClick={() => act('dec', () => providerBookingApi.respond(booking._id, 'decline')).then(onClose)}><X size={16} /> Decline</button>
          </div>
        </div>
      </section>
    </div>
  );
};

const ProviderBookings = () => {
  const { user } = useAuth();
  const [all, setAll] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [pills, setPills] = useState(['pending', 'active']);
  const [serviceFilter, setServiceFilter] = useState('all');
  const [respondBooking, setRespondBooking] = useState(null);

  const togglePill = (key) => setPills((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
  const selectedRef = useRef(selectedId);
  useEffect(() => { selectedRef.current = selectedId; }, [selectedId]);

  const loadAll = useCallback(async () => {
    try {
      const response = await providerBookingApi.list('all');
      setAll(response.data || []);
      setCounts(response.counts || {});
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't load bookings.");
    } finally {
      setLoading(false);
    }
  }, []);

  const openDetail = useCallback(async (id, silent = false) => {
    if (!silent) setDetailLoading(true);
    setSelectedId(id);
    try {
      const response = await providerBookingApi.detail(id);
      setDetail(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't load booking.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const socket = getSocket() || connectSocket();
    if (!socket) return undefined;
    const refresh = () => {
      loadAll();
      if (selectedRef.current) openDetail(selectedRef.current, true);
    };
    socket.on('booking:new', refresh);
    socket.on('booking:updated', refresh);
    return () => {
      socket.off('booking:new', refresh);
      socket.off('booking:updated', refresh);
    };
  }, [loadAll, openDetail]);

  const act = async (key, fn) => {
    setBusy(key);
    setError('');
    try {
      await fn();
      await loadAll();
      if (selectedRef.current) await openDetail(selectedRef.current, true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong.');
    } finally {
      setBusy('');
    }
  };

  const back = () => { setSelectedId(null); setDetail(null); };
  const services = useMemo(() => Array.from(new Set(all.map((booking) => booking.service?.name).filter(Boolean))).sort(), [all]);
  const earnings = useMemo(() => all.filter((booking) => booking.status === 'completed').reduce((sum, booking) => sum + (Number(booking.payout) || 0), 0), [all]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = all.filter((booking) => {
      if (pills.length && !pills.includes(groupOf(booking.status))) return false;
      if (serviceFilter !== 'all' && booking.service?.name !== serviceFilter) return false;
      if (query && !`${booking.service?.name || ''} ${booking.clientName || ''}`.toLowerCase().includes(query)) return false;
      return true;
    });
    return list.sort((a, b) => {
      const groupA = GROUP_ORDER[groupOf(a.status)] ?? 4;
      const groupB = GROUP_ORDER[groupOf(b.status)] ?? 4;
      if (groupA !== groupB) return groupA - groupB;
      return new Date(a.scheduledDate) - new Date(b.scheduledDate);
    });
  }, [all, pills, serviceFilter, search]);

  if (selectedId) {
    return (
      <ProviderLayout title="Bookings">
        {detailLoading && !detail ? (
          <div className="px-4 py-12 text-center font-dm text-giggs-muted">Loading…</div>
        ) : detail ? (
          <>
            {error && <div className="mb-4 rounded-lg border border-red-500/20 bg-red-500/[0.08] px-3.5 py-2.5 font-dm text-sm text-red-500">{error}</div>}
            <JobDetail booking={detail} busy={busy} act={act} onBack={back} myId={user?._id} />
          </>
        ) : (
          <div className="rounded-lg border border-red-500/20 bg-red-500/[0.08] px-3.5 py-2.5 font-dm text-sm text-red-500">Couldn't load this booking.</div>
        )}
      </ProviderLayout>
    );
  }

  const stats = [
    { label: 'Needs response', value: counts.pending || 0, Icon: Clock, tint: 'bg-amber-500/10 text-amber-700' },
    { label: 'Active jobs', value: counts.active || 0, Icon: Briefcase, tint: 'bg-[#185FA5]/10 text-[#185FA5]' },
    { label: 'Completed', value: counts.completed || 0, Icon: CheckCircle2, tint: 'bg-giggs-green/[0.08] text-[#1a7d56]' },
    { label: 'Earnings', value: `$${earnings.toFixed(2)}`, Icon: DollarSign, tint: 'bg-giggs-green/[0.08] text-[#1a7d56]' },
  ];

  return (
    <ProviderLayout title="Bookings">
      <div className="font-dm text-[#2D3B35]">
        <div className="mb-5">
          <h1 className="m-0 font-nunito text-2xl font-extrabold text-[#10271d]">Bookings</h1>
          <p className="mb-0 mt-1 text-[13.5px] text-giggs-muted">Respond to requests and manage your jobs.</p>
        </div>

        <div className="mb-5 grid grid-cols-[repeat(auto-fit,minmax(min(100%,190px),1fr))] gap-3.5">
          {stats.map(({ label, value, Icon, tint }) => (
            <div className="flex items-center gap-3.5 rounded-2xl border border-[#E3EAE6] bg-white px-[18px] py-4" key={label}>
              <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}><Icon size={20} /></div>
              <div className="min-w-0"><div className="text-xs font-semibold text-giggs-muted">{label}</div><div className="mt-px truncate font-nunito text-[23px] font-extrabold leading-tight text-[#10271d]">{value}</div></div>
            </div>
          ))}
        </div>

        <div className="mb-4 flex max-w-full gap-1.5 overflow-x-auto pb-1">
          {STATUS_PILLS.map((pill) => {
            const count = counts[pill.key] || 0;
            const active = pills.includes(pill.key);
            return (
              <button type="button" key={pill.key} className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${active ? 'border-giggs-green bg-giggs-green text-white' : 'border-[#DCE4DF] bg-white text-[#6B7F75] hover:border-giggs-green/40'}`} onClick={() => togglePill(pill.key)}>
                {pill.label}{count > 0 && <span className={`inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-slate-100 text-[#6B7F75]'}`}>{count}</span>}
              </button>
            );
          })}
        </div>

        <div className="mb-4 flex flex-col gap-2.5 sm:flex-row">
          <label className="relative min-w-0 flex-1">
            <Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-giggs-muted" />
            <input className="box-border w-full rounded-[10px] border border-[#D5DFD9] bg-white py-2.5 pl-10 pr-3.5 text-[13px] text-giggs-ink outline-none transition focus:border-giggs-green focus:ring-2 focus:ring-giggs-green/10" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by client or service" />
          </label>
          <select className="box-border w-full cursor-pointer rounded-[10px] border border-[#D5DFD9] bg-white px-3.5 py-2.5 text-[13px] text-giggs-text outline-none transition focus:border-giggs-green sm:w-auto sm:min-w-[180px]" value={serviceFilter} onChange={(event) => setServiceFilter(event.target.value)}>
            <option value="all">All services</option>
            {services.map((service) => <option key={service} value={service}>{service}</option>)}
          </select>
        </div>

        {error && <div className="mb-4 rounded-lg border border-red-500/20 bg-red-500/[0.08] px-3.5 py-2.5 text-sm text-red-500">{error}</div>}

        <section className="overflow-hidden rounded-2xl border border-[#E3EAE6] bg-white">
          {loading ? (
            <div className="px-4 py-10 text-center text-[13px] text-giggs-muted">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-[13px] text-giggs-muted"><strong className="mb-1 block text-[#10271d]">No bookings match</strong>Try clearing the filters or search.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] border-collapse text-[13px]">
                <thead className="bg-slate-50"><tr>{['Service', 'Client', 'Date & time', 'Location', 'Status', 'Payout', ''].map((heading, index) => <th key={`${heading}-${index}`} className={`border-b border-[#E3EAE6] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.04em] text-giggs-muted ${index === 5 ? 'text-right' : ''} ${index === 2 || index === 3 ? 'hidden md:table-cell' : ''}`}>{heading}</th>)}</tr></thead>
                <tbody>
                  {filtered.map((booking) => {
                    const action = booking.status === 'pending' ? 'Respond' : ['accepted', 'in_progress'].includes(booking.status) ? 'Manage' : 'View';
                    return (
                      <tr key={booking._id} className="cursor-pointer border-b border-[#EEF2EF] last:border-b-0 hover:bg-slate-50/70" onClick={() => booking.status === 'pending' ? setRespondBooking(booking) : openDetail(booking._id)}>
                        <td className="px-4 py-3.5"><div className="flex min-w-0 items-center gap-2 font-semibold text-[#10271d]"><span className="shrink-0 text-giggs-green"><ServiceIcon serviceId={booking.service?.icon || booking.service?.slug} size={17} /></span><span className="max-w-[180px] truncate">{booking.service?.name}</span></div></td>
                        <td className="px-4 py-3.5"><span className="font-medium text-[#41584d]">{booking.clientName}</span></td>
                        <td className="hidden whitespace-nowrap px-4 py-3.5 text-[#41584d] md:table-cell">{fmtDate(booking.scheduledDate)}<div className="mt-0.5 text-[11px] text-giggs-muted">{booking.startTime}–{booking.endTime}</div></td>
                        <td className="hidden whitespace-nowrap px-4 py-3.5 text-[#41584d] md:table-cell">{[booking.location?.suburb, booking.location?.state].filter(Boolean).join(' ')}</td>
                        <td className="px-4 py-3.5"><StatusBadge status={booking.status} /></td>
                        <td className="whitespace-nowrap px-4 py-3.5 text-right font-bold text-[#10271d]">${booking.payout}</td>
                        <td className="px-4 py-3.5"><span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-giggs-green">{action} <ChevronRight size={15} /></span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {respondBooking && <RespondModal booking={respondBooking} busy={busy} act={act} onClose={() => setRespondBooking(null)} />}
      </div>
    </ProviderLayout>
  );
};

export default ProviderBookings;